/**
 * Providers List Page
 * Main page for listing and managing service providers
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Chip,
  Tooltip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  FileDownload as ExportIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  MedicalServices as ProviderIcon,
  ToggleOff as InactivateIcon,
  ToggleOn as ActivateIcon,
  LocalHospital as ClinicaIcon,
  Science as LaboratorioIcon,
  Business as HospitalIcon,
  Person as ConsultorioIcon,
  MedicalInformation as CentroMedicoIcon,
  Park as AmbientalIcon,
  Security as SegurancaIcon,
  Category as MultiploIcon,
  Schedule as HoraMarcadaIcon,
  Groups as OrdemChegadaIcon,
  Biotech as ManageExamsIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';

// Components
import ProviderFilters from './components/ProviderFilters';
import { pode } from '../../authorization/pode';
import { useAcao, useControle } from '../../authorization/useVeredito';
import { PageShell, DataTable, type DataTableColumn } from '../../components';
import { RouteActionButton } from '../../components/common';

// Services and types
import { providerService, Provider, ProviderFilters as ProviderFiltersType } from '../../services/providerService';
import { ProviderStatus, ProviderType, ServiceType } from '../../types/provider';

// Hooks
import { useProviderExport } from '../../hooks/useProviderExport';

interface ProviderFiltersData {
  searchTerm: string;
  status: string[];
  providerTypes: string[];
}

// Status labels and colors
const STATUS_LABELS: Record<ProviderStatus, string> = {
  ATIVO: 'Ativo',
  INATIVO: 'Inativo',
  SUSPENSO: 'Suspenso',
  BLOQUEADO: 'Bloqueado',
  PENDENTE: 'Pendente',
};

const STATUS_COLORS: Record<ProviderStatus, 'success' | 'default' | 'warning' | 'error' | 'info'> = {
  ATIVO: 'success',
  INATIVO: 'default',
  SUSPENSO: 'warning',
  BLOQUEADO: 'error',
  PENDENTE: 'info',
};

// Provider type labels and icons
const TYPE_LABELS: Record<ProviderType, string> = {
  CLINICA: 'Clínica',
  LABORATORIO: 'Laboratório',
  HOSPITAL: 'Hospital',
  CONSULTORIO: 'Consultório',
  CENTRO_MEDICO: 'Centro Médico',
  AMBIENTAL: 'Ambiental',
  SEGURANCA: 'Segurança',
  MULTIPLO: 'Múltiplo',
};

const TYPE_COLORS: Record<ProviderType, 'primary' | 'secondary' | 'success' | 'warning' | 'info' | 'error' | 'default'> = {
  CLINICA: 'primary',
  LABORATORIO: 'secondary',
  HOSPITAL: 'error',
  CONSULTORIO: 'info',
  CENTRO_MEDICO: 'success',
  AMBIENTAL: 'success',
  SEGURANCA: 'warning',
  MULTIPLO: 'default',
};

const getTypeIcon = (type: ProviderType) => {
  const icons: Record<ProviderType, React.ReactElement> = {
    CLINICA: <ClinicaIcon fontSize="small" />,
    LABORATORIO: <LaboratorioIcon fontSize="small" />,
    HOSPITAL: <HospitalIcon fontSize="small" />,
    CONSULTORIO: <ConsultorioIcon fontSize="small" />,
    CENTRO_MEDICO: <CentroMedicoIcon fontSize="small" />,
    AMBIENTAL: <AmbientalIcon fontSize="small" />,
    SEGURANCA: <SegurancaIcon fontSize="small" />,
    MULTIPLO: <MultiploIcon fontSize="small" />,
  };
  return icons[type] || <ProviderIcon fontSize="small" />;
};

const isProviderActive = (provider: Provider) => provider.status === ProviderStatus.ATIVO;

const getDisplayName = (provider: Provider) => provider.tradeName || provider.legalName;

// A clínica prospectada não tem CNPJ: o traço diz isso, a célula em branco parece defeito.
const SEM_VALOR = '—';

const formatCNPJ = (cnpj: string | null | undefined) => {
  if (!cnpj) return SEM_VALOR;
  const clean = cnpj.replace(/\D/g, '');
  if (clean.length !== 14) return cnpj;
  return clean.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
};

const formatarLogradouro = (provider: Provider) => {
  const rua = [provider.address, provider.addressNumber].filter(Boolean).join(', ');
  return [rua, provider.neighborhood].filter(Boolean).join(' — ');
};

const formatarRecebimento = (recebidaEm: string | Date | undefined) => {
  if (!recebidaEm) return SEM_VALOR;
  const data = new Date(recebidaEm);
  if (Number.isNaN(data.getTime())) return SEM_VALOR;
  return data.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const columns: DataTableColumn<Provider>[] = [
  {
    id: 'provider',
    label: 'Prestador',
    primary: true,
    render: (provider) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
          {getTypeIcon(provider.providerType)}
        </Avatar>
        <Box>
          <Typography variant="subtitle2" fontWeight="bold">
            {getDisplayName(provider)}
          </Typography>
          {provider.tradeName && provider.tradeName !== provider.legalName && (
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
              {provider.legalName}
            </Typography>
          )}
        </Box>
      </Box>
    ),
  },
  {
    id: 'cnpj',
    label: 'CNPJ',
    render: (provider) => (
      <Typography variant="body2" fontFamily="monospace">
        {formatCNPJ(provider.cnpj)}
      </Typography>
    ),
  },
  {
    id: 'type',
    label: 'Tipo',
    render: (provider) => (
      <Tooltip title={TYPE_LABELS[provider.providerType] || provider.providerType}>
        <Chip
          icon={getTypeIcon(provider.providerType)}
          label={TYPE_LABELS[provider.providerType] || provider.providerType}
          size="small"
          color={TYPE_COLORS[provider.providerType] || 'default'}
          variant="outlined"
        />
      </Tooltip>
    ),
  },
  {
    id: 'serviceType',
    label: 'Atendimento',
    render: (provider) =>
      provider.serviceType ? (
        <Chip
          icon={provider.serviceType === ServiceType.ORDEM_CHEGADA ? <OrdemChegadaIcon fontSize="small" /> : <HoraMarcadaIcon fontSize="small" />}
          label={provider.serviceType}
          size="small"
          color={provider.serviceType === ServiceType.ORDEM_CHEGADA ? 'warning' : 'info'}
          variant="outlined"
        />
      ) : (
        <Typography variant="body2" color="text.secondary">-</Typography>
      ),
  },
  {
    id: 'status',
    label: 'Status',
    render: (provider) => (
      <Chip
        label={STATUS_LABELS[provider.status] || provider.status}
        size="small"
        color={STATUS_COLORS[provider.status] || 'default'}
      />
    ),
  },
  {
    id: 'location',
    label: 'Localização',
    render: (provider) => (
      <Typography variant="body2">
        {provider.city ? `${provider.city}, ` : ''}{provider.state ?? '-'}
      </Typography>
    ),
  },
  {
    id: 'contact',
    label: 'Contato',
    render: (provider) =>
      provider.contactEmail ? (
        <Box>
          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
            {provider.contactName ?? '-'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {provider.contactEmail}
          </Typography>
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary">-</Typography>
      ),
  },
];

// O recorte das prospectadas mostra outras colunas de propósito: quem olha uma clínica que ainda
// não é prestador precisa do contato e de quando ela chegou, não de situação e tipo.
const colunasDeProspectadas: DataTableColumn<Provider>[] = [
  {
    id: 'clinica',
    label: 'Clínica',
    primary: true,
    render: (provider) => (
      <Box>
        <Typography variant="subtitle2" fontWeight="bold">
          {getDisplayName(provider)}
        </Typography>
        {formatarLogradouro(provider) && (
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
            {formatarLogradouro(provider)}
          </Typography>
        )}
      </Box>
    ),
  },
  {
    id: 'cnpj',
    label: 'CNPJ',
    render: (provider) => (
      <Typography variant="body2" fontFamily="monospace" color="text.secondary">
        {formatCNPJ(provider.cnpj)}
      </Typography>
    ),
  },
  {
    id: 'contato',
    label: 'Contato',
    render: (provider) => (
      <Typography variant="body2" fontFamily="monospace">
        {provider.contactPhone || provider.receptionPhone || SEM_VALOR}
      </Typography>
    ),
  },
  {
    id: 'cidade',
    label: 'Cidade',
    render: (provider) => <Typography variant="body2">{provider.city || SEM_VALOR}</Typography>,
  },
  {
    id: 'recebidaEm',
    label: 'Recebida em',
    render: (provider) => (
      <Typography variant="body2">{formatarRecebimento(provider.createdAt)}</Typography>
    ),
  },
];

const ProvidersListPage: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const visualizar = useControle('/providers/:id', pode('provider.read'), 'Visualizar');
  const editar = useControle('/providers/:id/edit', pode('provider.update'), 'Editar');
  const gerenciarExames = useControle(
    '/providers/:providerId/exams',
    pode('provider_exam.read'),
    'Gerenciar Exames'
  );
  const horarios = useControle(
    '/providers/weekly-schedule',
    pode('provider.read'),
    'Horários de Atendimento'
  );
  const completar = useControle(
    '/providers/:id/edit',
    pode('provider.update'),
    'Completar cadastro'
  );
  const inativar = useAcao('provider.update', 'Inativar');
  const ativar = useAcao('provider.update', 'Ativar');
  const { exporting, exportProviders } = useProviderExport();

  // State
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [currentFilters, setCurrentFilters] = useState<Partial<ProviderFiltersType>>({ status: 'ATIVO' });
  const [searchTerm, setSearchTerm] = useState('');
  const [clinicasProspectadas, setClinicasProspectadas] = useState(0);
  const [apenasProspectadas, setApenasProspectadas] = useState(false);

  // Toggle status confirmation dialog state
  const [toggleDialogOpen, setToggleDialogOpen] = useState(false);
  const [providerToToggle, setProviderToToggle] = useState<Provider | null>(null);

  // O recorte das prospectadas é o que a consulta pergunta, e não um filtro a mais somado aos
  // outros: por isso ele apaga a situação em vez de conviver com ela.
  const filtrosDaConsulta = useMemo(
    () =>
      apenasProspectadas
        ? { ...currentFilters, status: undefined, apenasEmbrionarias: true }
        : currentFilters,
    [apenasProspectadas, currentFilters]
  );

  // Load providers from API
  const loadProviders = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await providerService.getProviders({
        page,
        limit: rowsPerPage,
        search: searchTerm,
        filters: filtrosDaConsulta,
      });
      setProviders(response.data);
      setTotalCount(response.pagination.total);
    } catch (err: unknown) {
      const apiError = err as { response?: { data?: { error?: string } }; message?: string };
      const errorMessage = (apiError.response?.data?.error || apiError.message) ?? 'Erro ao carregar prestadores';
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchTerm, filtrosDaConsulta, enqueueSnackbar]);

  // Apply filters
  const applyFilters = (filters: ProviderFiltersData) => {
    const apiFilters: Partial<ProviderFiltersType> = {};
    if (filters.status.length > 0) {
      apiFilters.status = filters.status.join(',');
    }
    if (filters.providerTypes.length > 0) {
      apiFilters.providerType = filters.providerTypes.join(',');
    }

    setCurrentFilters(apiFilters);
    setSearchTerm(filters.searchTerm);
    setPage(1); // Reset to first page when filtering
  };

  // Handle view provider
  const handleViewProvider = (provider: Provider) => {
    navigate(`/providers/${provider.id}`);
  };

  // Handle edit provider
  const handleEditProvider = (provider: Provider) => {
    navigate(`/providers/${provider.id}/edit`);
  };

  // Handle toggle provider status
  const handleToggleStatus = async (provider: Provider) => {
    try {
      const providerId = provider.id.toString();
      await providerService.toggleProviderStatus(providerId);
      const isActive = provider.status === 'ATIVO';
      enqueueSnackbar(`Prestador ${isActive ? 'inativado' : 'ativado'} com sucesso`, { variant: 'success' });
      loadProviders(); // Reload data
    } catch (err: unknown) {
      const apiError = err as { response?: { data?: { error?: string } } };
      const errorMessage = apiError.response?.data?.error || 'Erro ao alterar status do prestador';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  const handleToggleStatusClick = (provider: Provider) => {
    setProviderToToggle(provider);
    setToggleDialogOpen(true);
  };

  const handleToggleStatusConfirm = () => {
    if (providerToToggle) {
      handleToggleStatus(providerToToggle);
    }
    setToggleDialogOpen(false);
    setProviderToToggle(null);
  };

  const handleToggleStatusCancel = () => {
    setToggleDialogOpen(false);
    setProviderToToggle(null);
  };

  // Handle manage exams
  const handleManageExams = (provider: Provider) => {
    navigate(`/providers/${provider.id}/exams`);
  };

  // Handle manage schedule
  const handleManageSchedule = (provider: Provider) => {
    navigate(`/providers/weekly-schedule?providerId=${provider.id}`);
  };

  // Handle create provider
  // Load data on mount and when dependencies change
  useEffect(() => {
    loadProviders();
  }, [loadProviders]);

  // A contagem é o que decide se o recorte existe na tela. Falhar aqui não pode derrubar a
  // listagem: sem contagem, a tela é a de sempre.
  useEffect(() => {
    let ativo = true;
    providerService
      .getStats()
      .then((stats) => {
        if (ativo) setClinicasProspectadas(stats.embrionarias ?? 0);
      })
      .catch(() => undefined);
    return () => {
      ativo = false;
    };
  }, []);

  const alternarRecorte = (apenas: boolean) => {
    setApenasProspectadas(apenas);
    setPage(1);
  };

  // "ATIVO" já vem marcado ao abrir a tela: só conta como filtro o que a pessoa mudou.
  const filtersActive =
    searchTerm.trim() !== '' ||
    (currentFilters.status ?? 'ATIVO') !== 'ATIVO' ||
    Boolean(currentFilters.providerType);

  return (
    <PageShell
      breadcrumbs={[
        { label: 'Início', path: '/' },
        { label: 'Prestadores' },
      ]}
      title="Prestadores"
      actions={
        <>
          <Button
            variant="outlined"
            startIcon={<ExportIcon />}
            onClick={() => exportProviders(filtrosDaConsulta)}
            disabled={exporting}
          >
            Exportar
          </Button>
          <RouteActionButton
            para="/providers/create"
            exige={pode('provider.create')}
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
          >
            Novo Prestador
          </RouteActionButton>
        </>
      }
    >
      {error && !loading && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Box sx={{ mb: 3 }}>
        <ProviderFilters
          onFilter={applyFilters}
          loading={loading}
          total={totalCount}
          mostrarRecorteDeProspectadas={clinicasProspectadas > 0}
          apenasProspectadas={apenasProspectadas}
          onRecorteChange={alternarRecorte}
        />
        {apenasProspectadas && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Mostrando apenas clínicas trazidas pela integração. O filtro de situação foi limpo.
          </Typography>
        )}
      </Box>

      {/* Providers Table */}
      <DataTable
        columns={apenasProspectadas ? colunasDeProspectadas : columns}
        rows={providers}
        getRowId={(provider) => provider.id}
        loading={loading}
        emptyState={
          apenasProspectadas
            ? {
                title: 'Nenhuma clínica prospectada neste recorte',
                description:
                  'As clínicas que a integração traz aparecem aqui até alguém completar o cadastro delas.',
              }
            : {
                title: 'Nenhum prestador no seu acesso',
                description:
                  'O prestador é a clínica ou o laboratório que realiza os exames e as consultas.',
                action: {
                  label: 'Cadastrar prestador',
                  node: 'providers',
                  deniedHint: 'Peça ao administrador do sistema para cadastrar um prestador.',
                },
              }
        }
        filtersActive={filtersActive}
        actions={[
          {
            icon: <EditIcon fontSize="small" />,
            color: 'primary',
            onClick: handleEditProvider,
            hidden: () => !apenasProspectadas,
            ...completar,
          },
          {
            icon: <VisibilityIcon fontSize="small" />,
            onClick: handleViewProvider,
            hidden: () => apenasProspectadas,
            ...visualizar,
          },
          {
            icon: <EditIcon fontSize="small" />,
            onClick: handleEditProvider,
            hidden: () => apenasProspectadas,
            ...editar,
          },
          {
            icon: <ManageExamsIcon fontSize="small" />,
            color: 'secondary',
            onClick: handleManageExams,
            hidden: () => apenasProspectadas,
            ...gerenciarExames,
          },
          {
            icon: <HoraMarcadaIcon fontSize="small" />,
            color: 'info',
            onClick: handleManageSchedule,
            hidden: (provider) =>
              apenasProspectadas || provider.serviceType !== ServiceType.AGENDA_CLINICA,
            ...horarios,
          },
          {
            icon: <InactivateIcon fontSize="small" />,
            color: 'warning',
            onClick: handleToggleStatusClick,
            hidden: (provider) => apenasProspectadas || !isProviderActive(provider),
            ...inativar,
          },
          {
            icon: <ActivateIcon fontSize="small" />,
            color: 'success',
            onClick: handleToggleStatusClick,
            hidden: (provider) => apenasProspectadas || isProviderActive(provider),
            ...ativar,
          },
        ]}
        pagination={{
          page: page - 1,
          rowsPerPage,
          totalCount,
          onPageChange: (newPage) => setPage(newPage + 1),
          onRowsPerPageChange: (newRowsPerPage) => {
            setPage(1);
            setRowsPerPage(newRowsPerPage);
          },
        }}
      />

      {/* Toggle Status Confirmation Dialog */}
      <Dialog open={toggleDialogOpen} onClose={handleToggleStatusCancel} maxWidth="sm" fullWidth>
        <DialogTitle>
          Confirmar {providerToToggle && isProviderActive(providerToToggle) ? 'Inativação' : 'Ativação'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja{' '}
            {providerToToggle && isProviderActive(providerToToggle) ? 'inativar' : 'ativar'} o prestador{' '}
            <strong>{providerToToggle && getDisplayName(providerToToggle)}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {providerToToggle && isProviderActive(providerToToggle)
              ? 'O prestador será marcado como inativo e não aparecerá nas listagens padrão.'
              : 'O prestador será marcado como ativo e voltará a aparecer nas listagens.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleToggleStatusCancel}>Cancelar</Button>
          <Button
            onClick={handleToggleStatusConfirm}
            color={providerToToggle && isProviderActive(providerToToggle) ? 'warning' : 'success'}
            variant="contained"
          >
            {providerToToggle && isProviderActive(providerToToggle) ? 'Inativar' : 'Ativar'}
          </Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
};

export default ProvidersListPage;
